import { Address } from "@/services/types/domain";
import { Badge } from "@/components/ui/Badge";
/**
 * Format price with currency symbol
 */
export const formatPrice = (
    price: number,
    currency: string = 'PEN',
    locale: string = 'es-PE'
): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return formatter.format(price);
};
export const toNum = (v: any, d = 0) => {
    if (v === null || v === undefined || v === "") return d;
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : d;
};
/**
 * Format price without currency symbol
 */
export const formatPriceNumber = (
    price: number,
    locale: string = 'es-PE'
): string => {
    const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return formatter.format(price);
};

/**
 * Format rating number
 */
export const formatRating = (rating: number): string => {
    return rating.toFixed(1);
};

/**
 * Format date
 */
export const formatDate = (
    date: string | Date,
    locale: string = 'es-PE',
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }
): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const formatter = new Intl.DateTimeFormat(locale, options);

    return formatter.format(dateObj);
};

/**
 * Format date and time
 */
export const formatDateTime = (
    date: string | Date,
    locale: string = 'es-PE'
): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const formatter = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return formatter.format(dateObj);
};

/**
 * Format relative time (e.g., "hace 2 horas")
 */
/*export const formatRelativeTime = (date: string | Date, locale: string = 'es-PE'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    // Less than a minute
    if (diffInSeconds < 60) {
        return 'hace unos segundos';
    }

    // Less than an hour
    if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    }

    // Less than a day
    if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `hace ${hours} hora${hours !== 1 ? 's' : ''}`;
    }

    // Less than a week
    if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `hace ${days} día${days !== 1 ? 's' : ''}`;
    }

    // Less than a month
    if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return `hace ${weeks} semana${weeks !== 1 ? 's' : ''}`;
    }

    // Less than a year
    if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return `hace ${months} mes${months !== 1 ? 'es' : ''}`;
    }

    // Years
    const years = Math.floor(diffInSeconds / 31536000);
    return `hace ${years} año${years !== 1 ? 's' : ''}`;
};*/
export const formatRelativeTime = (date: string | Date, locale: string = 'es-PE'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (diffInSeconds < 60) {
        return rtf.format(-Math.floor(diffInSeconds), 'second');
    }

    if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return rtf.format(-minutes, 'minute');
    }

    if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return rtf.format(-hours, 'hour');
    }

    if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return rtf.format(-days, 'day');
    }

    if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return rtf.format(-weeks, 'week');
    }

    if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return rtf.format(-months, 'month');
    }

    const years = Math.floor(diffInSeconds / 31536000);
    return rtf.format(-years, 'year');
};

/**
 * Format number with abbreviations (K, M, B)
 */
export const formatNumberAbbreviated = (num: number): string => {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format percentage
 */
export const formatPercentage = (
    value: number,
    decimals: number = 1
): string => {
    return `${value.toFixed(decimals)}%`;
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phone: string): string => {
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');

    // Format Peruvian phone number
    if (cleaned.length === 9) {
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    }

    // Return original if format doesn't match
    return phone;
};

/**
 * Format order number
 */
export const formatOrderNumber = (orderNumber: string): string => {
    return orderNumber.toUpperCase();
};

/**
 * Format shipping status
 */
export const formatShippingStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
        pending: 'Pendiente',
        processing: 'Procesando',
        shipped: 'Enviado',
        delivered: 'Entregado',
        cancelled: 'Cancelado',
        refunded: 'Reembolsado',
    };

    return statusMap[status] || status;
};

/**
 * Format payment status
 */
export const formatPaymentStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
        pending: 'Pendiente',
        paid: 'Pagado',
        failed: 'Fallido',
        refunded: 'Reembolsado',
        partially_refunded: 'Parcialmente reembolsado',
    };

    return statusMap[status] || status;
};

/**
 * Format discount
 */
export const formatDiscount = (originalPrice: number, salePrice: number): string => {
    const discount = ((originalPrice - salePrice) / originalPrice) * 100;
    return `${Math.round(discount)}% OFF`;
};

/**
 * Format weight
 */
export const formatWeight = (weight: number, unit: string = 'kg'): string => {
    if (weight < 1 && unit === 'kg') {
        return `${(weight * 1000).toFixed(0)}g`;
    }
    return `${weight.toFixed(1)}${unit}`;
};

/**
 * Format dimensions
 */
export const formatDimensions = (
    length: number,
    width: number,
    height: number,
    unit: string = 'cm'
): string => {
    return `${length} × ${width} × ${height} ${unit}`;
};

/**
 * Truncate text
 */
export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
};

/**
 * Format URL slug
 */
export const formatSlug = (text: string): string => {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim()
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Format search query for URL
 */
export const formatSearchQuery = (query: string): string => {
    return encodeURIComponent(query.trim());
};

/**
 * Format review count
 */
export const formatReviewCount = (count: number): string => {
    if (count === 0) return 'Sin reseñas';
    if (count === 1) return '1 reseña';
    return `${formatNumberAbbreviated(count)} reseñas`;
};

/**
 * Format stock quantity
 */
export const formatStockQuantity = (quantity: number): string => {
    if (quantity === 0) return 'Sin stock';
    if (quantity === 1) return '¡Última unidad!';
    if (quantity <= 5) return `Solo quedan ${quantity}`;
    if (quantity <= 10) return `Pocas unidades (${quantity})`;
    return 'En stock';
};
export const parseAddress = (raw: string | null | undefined): Address | null => {
    if (!raw) return null;

    try {
        return JSON.parse(raw) as Address;
    } catch {
        // Fallback: eliminar backslashes que no preceden a caracteres válidos de escape
        try {
            const fixed = raw.replace(/\\(?!["\\/bfnrtu])/g, "");
            return JSON.parse(fixed) as Address;
        } catch {
            return null;
        }
    }
};

const statusMap = {
    pending: "Pendiente",
    processing: "Procesando",
    shipped: "Enviado",
    delivered: "Entregado",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
};

const paymentMap = {
    pending: "Pendiente",
    paid: "Pagado",
    failed: "Fallido",
    refunded: "Reembolsado",
};
const statusRequestSellerMap = {
    pending: "Pendiente",
    approved: "Aprobado",
    rejected: "Rechazado",
    verified: "Verificado",
}
const statusUserMap = {
    active: "Activo",
    inactive: "Inactivo",
    suspended: "Suspendido",
    banned: "Baneado"
}
const typeUserMap = {
    customer: "Cliente",
    seller: "Vendedor",
    admin: "Administrador"
}
export const tOrderStatus = (s: string) => statusMap[s as keyof typeof statusMap] ?? s;
export const tPaymentStatus = (s: string) => paymentMap[s as keyof typeof paymentMap] ?? s;
export const tRequestSellerStatus = (s: string) => statusRequestSellerMap[s as keyof typeof statusRequestSellerMap] ?? s;
export const tUserStatus = (s: string) => statusUserMap[s as keyof typeof statusUserMap] ?? s;
export const tUserType = (s: string) => typeUserMap[s as keyof typeof typeUserMap] ?? s;
type RefundStatus = "pending" | "approved" | "rejected" | null;
export const tRefundStatus = (s?: RefundStatus) => {
    if (!s) return "Sin solicitud";
    return s === "pending" ? "Reembolso Pend"
        : s === "approved" ? "Reembolso Apro"
            : "Reembolso rechazado";
};
export const refundVariant = (s?: RefundStatus) =>
    s === "pending" ? "warning"
        : s === "approved" ? "success"
            : s === "rejected" ? "destructive"
                : "outline";
export const orderVariant = (s: string): Parameters<typeof Badge>[0]["variant"] => {
    switch (s) {
        case "delivered":
            return "success";
        case "processing":
        case "shipped":
            return "secondary";
        case "cancelled":
            return "destructive";
        case "pending":
        default:
            return "warning";
    }
};

export const paymentVariant = (s: string): Parameters<typeof Badge>[0]["variant"] => {
    switch (s) {
        case "paid":
            return "success";
        case "failed":
            return "destructive";
        case "refunded":
            return "secondary";
        case "pending":
        default:
            return "warning";
    }
};

export const getUserStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (status) {
        case 'active':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'inactive':
            return 'bg-gray-100 text-gray-800 border-gray-200';
        case 'suspended':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'banned':
            return 'bg-red-100 text-red-800 border-red-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

export const getUserTypeColor = (userType: string | undefined) => {
    if (!userType) return 'bg-gray-100 text-gray-800';
    switch (userType) {
        case 'admin':
            return 'bg-purple-100 text-purple-800';
        case 'seller':
            return 'bg-blue-100 text-blue-800';
        case 'customer':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

export const getStatusSellerColor = (status: string) => {
    switch (status) {
        case 'pending':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'verified':
            return 'bg-green-100 text-green-800 border-green-200';
        case 'rejected':
            return 'bg-red-100 text-red-800 border-red-200';
        default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};